import PropTypes from 'prop-types';
import { always, compose, cond, includes, isNil, none, pathOr, T } from 'ramda';
import { useSelector } from 'react-redux';

import { getLabelMessage } from '@gef-ui/localization/messages';

import { codelistProvider, ComponentLoader, PKUrl, SidebarInfo, SidebarInfoColumn } from 'components';
import { useIgnoreErrorsWhen } from 'components/molecules/ComponentLoader/hooks';
import { codelists } from 'constants/codelists';
import fetch from 'features/common/fetch';
import taskDetail from 'features/views/taskDetail';
import { getDetail } from 'features/views/taskDetail/selectors';
import { equalOrThreeDashes, getCodelistPropById, toNormalDate } from 'utils';

const { LOSE_COMISSION } = taskDetail.constants.TASK_TYPES;

export const SidebarPolicyInfo = ({ products, policyStatuses }) => {
	const taskDetail = useSelector(getDetail);

	const getProductLines = cond([
		[
			(propValues) => includes(propValues.taskTypeId, [LOSE_COMISSION]),
			(propValues) => pathOr(equalOrThreeDashes(), ['common', 'insurance'], propValues),
		],
		[
			(propValues) => none(isNil, [propValues.productLines, propValues.products]),
			(propValues) =>
				propValues.productLines
					.map((pL) => getCodelistPropById(pL, propValues.products, 'shortDescription'))
					.join(', '),
		],
		[T, always('---')],
	]);

	const getPolicyStatus = cond([
		[
			(propValues) => includes(propValues.taskTypeId, [LOSE_COMISSION]),
			(propValues) => pathOr(equalOrThreeDashes(), ['common', 'policyStatus'], propValues),
		],
		[
			(propValues) => none(isNil, [propValues.policyStatuses, propValues.policyStatusId]),
			(propValues) => getCodelistPropById(propValues.policyStatusId, propValues.policyStatuses),
		],
		[T, always('---')],
	]);

	const { taskTypeId, metadata = {}, client = {}, policy = {}, claimNo, id, clientLoadedFromCcd } = taskDetail;
	const { common = {} } = metadata;
	const { id: partnerId } = client;
	const { policyNo, systemId, policyStatusId, productLines } = policy;

	const generateMainDateNegotiation = (taskTypeId) => {
		let finalDate;
		if (taskTypeId === LOSE_COMISSION) {
			finalDate = equalOrThreeDashes(toNormalDate(common.policyOpenDate));
		}

		if ([49, 50, 51, 52].includes(taskTypeId)) {
			if (policy.policyNo) {
				finalDate = equalOrThreeDashes(toNormalDate(policy.policyOpenDate));
			} else {
				finalDate = equalOrThreeDashes(toNormalDate(common.policyOpenDate));
			}
		} else {
			// 44, 46 and other types
			finalDate = equalOrThreeDashes(toNormalDate(policy.policyOpenDate));
		}

		return finalDate;
	};

	const ignoreErrorsForTaskDetailUserHasNoAccess = useIgnoreErrorsWhen({
		fetchStorePath: fetch.constants.urls.taskDetail,
		attributePath: ['metaData', 'parsedContent', 'status'],
		value: 403,
	});

	return (
		<ComponentLoader
			fetchStorePath={fetch.constants.urls.taskDetail}
			ignoreErrors={ignoreErrorsForTaskDetailUserHasNoAccess}
		>
			<SidebarInfo>
				<SidebarInfoColumn
					attribute={getLabelMessage('detail:asideBarMainPSNumber')}
					value={
						<PKUrl.PKPolicy
							partnerId={partnerId}
							policyNo={policyNo}
							systemId={clientLoadedFromCcd && systemId}
							taskId={id}
							unlock
							muiLink
						>
							{equalOrThreeDashes(policyNo)}
						</PKUrl.PKPolicy>
					}
				/>
				<SidebarInfoColumn
					attribute={getLabelMessage('detail:asideBarMainProposalNumber')}
					value={equalOrThreeDashes(policy.proposalNo)}
				/>
				<SidebarInfoColumn
					attribute={getLabelMessage('detail:asideBarMainInsurance')}
					value={getProductLines({ taskTypeId, products, productLines, common })}
				/>
				<SidebarInfoColumn
					attribute={getLabelMessage('detail:asideBarMainPSStatus')}
					value={getPolicyStatus({ taskTypeId, policyStatuses, policyStatusId, common })}
				/>
				<SidebarInfoColumn
					attribute={getLabelMessage('detail:asideBarMainDateNegotiation')}
					value={generateMainDateNegotiation(taskTypeId)}
				/>
				<SidebarInfoColumn
					attribute={getLabelMessage('detail:asideBarMainDateBegining')}
					value={
						includes(taskTypeId, [LOSE_COMISSION])
							? equalOrThreeDashes(toNormalDate(common.policyValueDate))
							: equalOrThreeDashes(toNormalDate(policy.policyValueDate))
					}
				/>
				<SidebarInfoColumn
					attribute={getLabelMessage('detail:asideBarMainDateEnd')}
					value={
						includes(taskTypeId, [LOSE_COMISSION])
							? equalOrThreeDashes(toNormalDate(common.policyFinalDate))
							: equalOrThreeDashes(toNormalDate(policy.policyFinalDate))
					}
				/>
				{!isNil(claimNo) && (
					<SidebarInfoColumn attribute={getLabelMessage('detail:asideBarClaimNo')} value={claimNo} />
				)}
			</SidebarInfo>
		</ComponentLoader>
	);
};

SidebarPolicyInfo.propTypes = {
	policyStatuses: PropTypes.array,
	products: PropTypes.array,
};

export default compose(
	codelistProvider(codelists.products, 'products'),
	codelistProvider(codelists.policyStatuses, 'policyStatuses'),
)(SidebarPolicyInfo);
